import React, { FC, useEffect, useState } from 'react'
import { change, Field, InjectedFormProps, reduxForm } from 'redux-form'
import { Form, Collapse, Row, Col, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import SwitchField from '../../../atoms/SwitchField'
import ColorPickerField from '../../../atoms/ColorPickerField'

// interfaces
import { IFormItemData, IItem } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT, RADIO_BTN_GROUP_FIELD, VALIDATIONS_TYPES } from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

// modals
import ValidationModal from '../../modals/ValidationModal'

// validations
import validateInputElementForm from '../validations/validateInputElementForm'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}

type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
		elementType?: string
	}

const InputElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const { handleSubmit, onRemoveItem } = props
	const dispatch = useDispatch()
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)
	const formItemId = useSelector((state: RootState) => state.form?.[FORM.INPUT_ELEMENT_FORM]?.values?.i)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.INPUT_ELEMENT_FORM]?.values)
	const validableComponents = useSelector((state: RootState) => state.formTemplate.validableComponents)
	const procedures = useSelector((state: RootState) => state.formTemplate.procedures)
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const [validationTypes, setValidationTypes] = useState<IItem[]>([])

	// on init map validation types and set them into array
	useEffect(() => {
		setValidationTypes(Object.values(VALIDATIONS_TYPES).map((item) => ({ label: item, value: item })))
	}, [])

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Input')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
				</Row>
				<Row className={'ml-4 mr-4 mb-4'} justify={'center'}>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Column in table')}
							name={ELEMENT_ATTRIBUTES.TABLE_COLUMN}
							options={columns?.data}
							size={'medium'}
							getPopupContainer={(triggerNode: any) => triggerNode}
							disabled={disable}
						/>
					</Col>
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Size')} key='1'>
						<Row justify={'center'} className={'mb-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={true}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'mb-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Field size')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.SIZE}
									name={ELEMENT_ATTRIBUTES.SIZE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Label')} key='2'>
						<Field component={InputField} placeholder={t('loc:Enter label')} name={ELEMENT_ATTRIBUTES.LABEL} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col className={'h-34px'} span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.LABEL_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
						</Row>
						<Row justify={'center'} className={'mb-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Position')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.POSITION}
									name={ELEMENT_ATTRIBUTES.LABEL_POSITION}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Placeholder')} key='3'>
						<Field
							component={InputField}
							placeholder={t('loc:Placeholder text')}
							name={ELEMENT_ATTRIBUTES.PLACEHOLDER}
							size={'medium'}
							disabled={disable}
						/>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.PLACEHOLDER_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.PLACEHOLDER_TEXT_TYPE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Hint')} key='4'>
						<Field component={InputField} placeholder={t('loc:Hint text')} name={ELEMENT_ATTRIBUTES.HINT} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.HINT_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									placeholder={t('loc:Enter template name')}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.HINT_TEXT_TYPE}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Autocomplete')} key='5'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Active')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.IS_AUTOCOMPLETE} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						{formValues?.[ELEMENT_ATTRIBUTES.IS_AUTOCOMPLETE] && (
							<>
								<Row justify={'center'} className={'my-2 w-full'}>
									<Col span={11} className={'h-34px flex flex-col justify-center'}>
										<p className={'m-0'}>{t('loc:Only options')}</p>
									</Col>
									<Col span={2} />
									<Col span={11} className={'h-34px'}>
										<Field
											component={SwitchField}
											name={ELEMENT_ATTRIBUTES.AUTOCOMPLETE_CHOOSE_ONLY_FROM_OPTIONS}
											className={'float-right'}
											disabled={disable}
										/>
									</Col>
								</Row>
								<Col span={24}>
									<Field
										component={SelectField}
										label={t('loc:Load procedure')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										name={ELEMENT_ATTRIBUTES.PROCEDURE_AUTOCOMPLETE_DROPDOWN}
										options={procedures.data}
										loading={procedures.isLoading}
										size={'medium'}
										placeholder={t('loc:Select procedure')}
										disabled={disable}
										clearBtn={true}
										required
									/>
								</Col>
								<Col span={24}>
									<Field
										component={SelectField}
										label={t('loc:Fields for load procedure')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										options={validableComponents?.data}
										mode={'multiple'}
										name={ELEMENT_ATTRIBUTES.FIELDS_AUTOCOMPLETE_DROPDOWN}
										placeholder={t('loc:Select field')}
										size={'medium'}
										disabled={disable}
										clearBtn={true}
									/>
								</Col>
								<Col span={24}>
									<Field
										component={SelectField}
										label={t('loc:OnChange procedure')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										name={ELEMENT_ATTRIBUTES.PROCEDURE_AUTOCOMPLETE_CONFIRM}
										options={procedures.data}
										loading={procedures.isLoading}
										size={'medium'}
										placeholder={t('loc:Select procedure')}
										disabled={disable}
										clearBtn={true}
									/>
								</Col>
								<Col span={24} className={'mb-2'}>
									<Field
										component={SelectField}
										label={t('loc:Fields for onChange procedure')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										options={validableComponents?.data}
										mode={'multiple'}
										name={ELEMENT_ATTRIBUTES.FIELDS_AUTOCOMPLETE_CONFIRM}
										placeholder={t('loc:Select field')}
										size={'medium'}
										disabled={disable}
										clearBtn={true}
									/>
								</Col>
							</>
						)}
					</Panel>
					<Panel header={t('loc:Validations')} key='6'>
						<Col span={24}>
							<Field
								component={SelectField}
								getPopupContainer={(triggerNode: any) => triggerNode}
								name={ELEMENT_ATTRIBUTES.HTML_VALIDATION}
								options={validationTypes}
								size={'medium'}
								disabled={disable}
								clearBtn={true}
							/>
						</Col>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Required')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.REQUIRED} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Readonly')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.READ_ONLY} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								dispatch(change(FORM.VALIDATION_FORM, ELEMENT_ATTRIBUTES.READ_ONLY, formValues?.readOnly))
								dispatch(change(FORM.VALIDATION_FORM, ELEMENT_ATTRIBUTES.REQUIRED, formValues?.required))
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.INPUT_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.INPUT}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.INPUT_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateInputElementForm,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(InputElementForm)

export default form
